import { dataLayerPush, TDataLayerPushArgument } from './helpers';

export type IAnalyticsRestParams = Partial<Omit<TDataLayerPushArgument, 'category' | 'action' | 'label'>>;

interface IAnalytics {
    category: string,
    send: (action: string, label?: string, restParams?: IAnalyticsRestParams) => void,
}

abstract class BaseAnalytics implements IAnalytics {
    private sender: typeof dataLayerPush = dataLayerPush;
    public category = '';

    public send(action: string, label?: string, restParams?: IAnalyticsRestParams): void {
        if (!this.category) {
            throw new Error('The analytics category is not set. Add a public variable "category" on a child class');
        }

        this.sender({
            event: 'UAevent',
            category: this.category,
            action,
            label,
            ...restParams,
        });
    }

    protected sendEcommerce<TEcommerceProps>(params: TDataLayerPushArgument<TEcommerceProps>): void {
        this.sender(params);
    }

    // The core methods or properties do not need to be shown.
    static get excludedNames() {
        return ['sender', 'category'];
    }

    public toJSON(): unknown {
        // @ts-ignore
        const ownMethods = this.__proto__ instanceof BaseAnalytics
            // @ts-ignore
            ? this.getMethodsFromInstance(this.__proto__)
            : [];

        const properties = this.getMethodsFromInstance(this);

        return {
            _className: this.constructor.name,
            category: this.category,
            root: ownMethods,
            children: properties,
        };
    }

    private getMethodsFromInstance(instance: Object): Record<string, unknown> {
        return Object.keys(instance).reduce<Record<string, unknown>>((acc, key) => {
            if (BaseAnalytics.excludedNames.includes(key)) {
                return acc;
            }

            const entity = Reflect.getOwnPropertyDescriptor<typeof instance, string>(instance, key);

            if (entity) {
                if (typeof entity.value === 'function') {
                    // finding all `this.send(*)`
                    const sendCalls = entity.value
                        .toString()
                        .match(/.*this.send\(.*\);/gm);

                    // finding all arguments inside the `this.send(*)`
                    const sendArgs = sendCalls.map(
                        (call: string) => call
                            .replace(/\s\s/g, '')
                            .replace(/\);/, '')
                            .replace(/this\.send\(/, ''),
                    );

                    if (sendCalls.length > 1) {
                        sendArgs.forEach((args: string) => {
                            const _args = this.processArgs(args);
                            acc[`${key}->${_args[0]}`] = {
                                eventAction: _args[0],
                                labels: _args.slice(1),
                            };
                        });
                    } else {
                        const _args = this.processArgs(sendArgs[0]);
                        acc[key] = {
                            eventAction: _args[0],
                            labels: _args.slice(1),
                        };
                    }
                }

                if (entity.value instanceof BaseAnalytics) {
                    acc[key] = entity.value.toJSON();
                }
            }

            return acc;
        }, {});
    }

    private processArgs(_args: string) {
        const args = _args.split('", ');
        // eslint-disable-next-line no-useless-escape
        args[0] = args[0].replaceAll('\"', '');

        if (args.length > 1) {
            // eslint-disable-next-line no-useless-escape
            const isSecondArgConstantValue = args[1].match(/\"/);

            if (!isSecondArgConstantValue) {
                args[1] = `%${args[1]}`;
            } else {
                // eslint-disable-next-line no-useless-escape
                args[1] = args[1].replaceAll('\"', '');
            }
        }

        return args;
    }

    protected errorToString(error: unknown) {
        if (error instanceof Error) {
            return error.message;
        }
        return String(error);
    }
}

export default BaseAnalytics;
